import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { applicantIsComplete, applicantDisplayName, computeOrderSummary, applicantIncompleteReason, totalDays } from "@/lib/applicants";
import { ADD_ONS_BY_ID, addOnsAreComplete } from "@/lib/addons";
import { CheckCircle2, ChevronDown, ChevronUp, Edit3, ArrowRight, Clock, Zap, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { openPaddleCheckout } from "@/lib/paddle";

export default function ApplyReview() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [order, setOrder] = useState(null);
  const [openIdx, setOpenIdx] = useState(null);
  const [paying, setPaying] = useState(false);

  // Always start the Review screen scrolled to the top.
  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: "instant" }); }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate(`/login?redirect=/apply/review/${orderId}`); return; }
    api.get(`/orders/${orderId}`).then(({ data }) => {
      const isCustom = data.order_type === "custom";
      // For PACKAGE orders only: if add-ons need details and applicants are all
      // complete, bounce into the add-on step first. Custom orders collect their
      // info in the Build Your File form before checkout, so they never enter
      // the applicant wizard here.
      if (!isCustom) {
        const hasAddOns = (data.add_ons || []).length > 0;
        const addOnsFilled = addOnsAreComplete(data.add_ons || [], data.add_on_details || {});
        const applicantsAllComplete = (data.applicants || []).length === data.num_applicants && (data.applicants || []).every(applicantIsComplete);
        if (hasAddOns && !addOnsFilled && applicantsAllComplete && data.payment_status !== "paid") {
          const lastIdx = Math.max(0, (data.num_applicants || 1) - 1);
          navigate(`/apply/applicants/${orderId}/${lastIdx}?step=addons`, { replace: true });
          return;
        }
      }
      setOrder(data);
    }).catch(() => {
      toast.error("Order not found"); navigate("/dashboard");
    });
  }, [orderId, authLoading, user]);

  if (!order) {
    return (
      <div className="min-h-screen flex flex-col bg-[#f8fafc]">
        <Header />
        <main className="flex-1 grid place-items-center text-[#475569]">Loading…</main>
        <Footer />
      </div>
    );
  }

  const isCustomOrder = order.order_type === "custom";
  const applicants = order.applicants || [];
  // Custom orders (Build Your File) collect all info in the pre-checkout form.
  // Whether or not they include 'visa' in their selected_documents, the
  // applicant wizard is NEVER a prerequisite for payment: for non-visa custom
  // orders there is no applicant wizard at all, and for visa custom orders the
  // wizard is triggered AFTER payment. So we only gate on applicant
  // completeness for classic PACKAGE orders.
  const applicantsComplete = isCustomOrder
    ? true
    : (applicants.length === order.num_applicants && applicants.every(applicantIsComplete));
  const addOnsComplete = !(order.add_ons || []).length || addOnsAreComplete(order.add_ons, order.add_on_details || {});
  // Custom orders also skip the add-on gating here — their add-on details are
  // collected in the Build Your File form (or filled in post-payment).
  const allComplete = isCustomOrder ? true : (applicantsComplete && addOnsComplete);
  const isPaid = order.payment_status === "paid";

  const editApplicant = (i) => navigate(`/apply/applicants/${orderId}/${i}?from=review`);

  const startPayment = async () => {
    if (!allComplete) {
      toast.error("Please complete all applicant details first");
      return;
    }
    setPaying(true);
    try {
      const origin = window.location.origin;
      const { data: session } = await api.post("/payments/checkout", { order_id: orderId, origin_url: origin });
      // Paddle Billing overlay — webhook handles payment_status flip on our side.
      await openPaddleCheckout({
        transactionId: session.transaction_id || session.session_id,
        successUrl: `${origin}/payment/success?session_id=${encodeURIComponent(session.transaction_id || session.session_id)}&order_id=${encodeURIComponent(orderId)}`,
        customerEmail: user?.email,
      });
      setPaying(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to start payment");
      setPaying(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#f8fafc]">
      <Header />
      <main className="flex-1">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-10">
          <Link to="/dashboard" className="text-xs text-[#475569] hover:text-[#0f1b2d]">← Back to dashboard</Link>
          <h1 className="mt-2 text-3xl sm:text-4xl font-display font-bold text-[#0f1b2d]">Review your order</h1>
          <p className="mt-1 text-[#475569]">Make sure every detail is correct before paying.</p>

          {/* Order summary */}
          <div className="mt-6 bg-white border border-[#e2e8f0] rounded-lg p-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Stat
              label="Destination(s)"
              value={
                isCustomOrder
                  ? (order.destination_country || "—")
                  : (() => { const s = computeOrderSummary(order.applicants || []); return s.mainDestination ? (s.others.length ? `${s.mainDestination} (+${s.others.length} more)` : s.mainDestination) : "—"; })()
              }
            />
            <Stat label={isCustomOrder ? "Order type" : "Package"} value={isCustomOrder ? "Custom file" : order.package_name} />
            <Stat label="Delivery" value={order.delivery_type === "express" ? "Express (1h)" : "Normal (24h)"} icon={order.delivery_type === "express" ? Zap : Clock} />
          </div>

          {isCustomOrder ? (
            <div className="mt-8" data-testid="review-custom-summary">
              <h2 className="font-display font-bold text-xl text-[#0f1b2d]">Your custom file</h2>
              <div className="mt-4 bg-white border border-[#e2e8f0] rounded-lg p-5">
                <div className="text-xs uppercase tracking-widest text-[#64748b] font-semibold mb-2">Selected documents</div>
                <ul className="list-disc pl-5 space-y-1 text-[15px] text-[#0f1b2d]">
                  {(order.selected_documents || []).map((d) => (
                    <li key={d} data-testid={`review-custom-doc-${d}`}>{d.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}</li>
                  ))}
                  {(order.selected_documents || []).length === 0 && <li className="text-[#94a3b8] italic">No documents on file</li>}
                </ul>
                <p className="mt-4 text-xs text-[#64748b]">
                  All the details for these documents were captured in the Build Your File form. You can continue to payment now — our team will start preparing your file as soon as the payment clears.
                </p>
              </div>
            </div>
          ) : (
          /* Applicants list */
          <div className="mt-8">
            <h2 className="font-display font-bold text-xl text-[#0f1b2d]">Applicants ({applicants.length})</h2>
            <div className="mt-4 space-y-3" data-testid="review-applicants">
              {applicants.map((a, i) => {
                const complete = applicantIsComplete(a);
                const reasons = complete ? [] : applicantIncompleteReason(a);
                const open = openIdx === i;
                return (
                  <div key={a._uid || i} className="bg-white border border-[#e2e8f0] rounded-lg" data-testid={`review-applicant-${i}`}>
                    <div className="p-4 flex items-center gap-3 flex-wrap">
                      {complete ? <CheckCircle2 className="w-5 h-5 text-emerald-600" data-testid={`review-status-complete-${i}`} /> : <AlertTriangle className="w-5 h-5 text-amber-500" data-testid={`review-status-incomplete-${i}`} />}
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-bold text-[#0f1b2d]">Applicant {i + 1} — {applicantDisplayName(a) || <span className="text-[#94a3b8] italic">Not filled</span>}</div>
                        <div className="text-xs text-[#64748b]">{a.passport_number ? `Passport ${a.passport_number}` : "No passport yet"} · {a.nationality || "—"}</div>
                      </div>
                      <button onClick={() => setOpenIdx(open ? null : i)} className="text-sm text-[#475569] hover:text-[#0f1b2d] inline-flex items-center gap-1" data-testid={`review-toggle-${i}`}>
                        {open ? <><ChevronUp className="w-4 h-4" /> Hide</> : <><ChevronDown className="w-4 h-4" /> Details</>}
                      </button>
                      <button onClick={() => editApplicant(i)} className="text-sm font-semibold text-[#2563eb] hover:text-[#1d4ed8] inline-flex items-center gap-1" data-testid={`review-edit-${i}`}>
                        <Edit3 className="w-4 h-4" /> Edit
                      </button>
                    </div>
                    {!complete && reasons.length > 0 && (
                      <div className="px-4 pb-3 -mt-1" data-testid={`review-incomplete-${i}`}>
                        <div className="text-xs bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-amber-900">
                          <div className="font-semibold mb-0.5">Missing details</div>
                          <ul className="list-disc pl-4 space-y-0.5">
                            {reasons.map((r) => (
                              <li key={r.step}><span className="font-semibold">{r.stepLabel}:</span> {r.fields.join(", ")}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                    {open && <ApplicantBreakdown a={a} />}
                  </div>
                );
              })}
            </div>
          </div>
          )}

          {/* Add-ons section */}
          {(order.add_ons || []).length > 0 && (
            <div className="mt-8" data-testid="review-addons">
              <h2 className="font-display font-bold text-xl text-[#0f1b2d]">Add-ons ({order.add_ons.length})</h2>
              <div className="mt-4 space-y-3">
                {order.add_ons.map((id) => {
                  const a = ADD_ONS_BY_ID[id]; if (!a) return null;
                  const Icon = a.icon;
                  const required = ["noc","sponsor_letter","invitation_letter"].includes(id);
                  const filled = order.add_on_details?.[id] && Object.values(order.add_on_details[id]).some((v) => v);
                  return (
                    <div key={id} className="bg-white border border-[#e2e8f0] rounded-lg p-4 flex items-center gap-3 flex-wrap" data-testid={`review-addon-${id}`}>
                      {filled ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <AlertTriangle className="w-5 h-5 text-amber-500" />}
                      <Icon className="w-4 h-4 text-[#475569]" />
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-bold text-[#0f1b2d]">{a.name}</div>
                        <div className="text-xs text-[#64748b]">+${a.price} · {a.description}</div>
                      </div>
                      <button onClick={() => navigate(`/apply/applicants/${orderId}/${Math.max(0, (order.num_applicants || 1) - 1)}?step=addons`)} className="text-sm font-semibold text-[#2563eb] hover:text-[#1d4ed8] inline-flex items-center gap-1" data-testid={`review-edit-addon-${id}`}>
                        <Edit3 className="w-4 h-4" /> {filled ? "Edit" : "Fill in"}
                      </button>
                    </div>
                  );
                })}
              </div>
              {!addOnsComplete && (
                <div className="mt-3 text-xs bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-amber-900" data-testid="review-addons-incomplete">
                  Please fill in all add-on details before proceeding to payment.
                </div>
              )}
            </div>
          )}

          {/* Footer CTA */}
          <div className="mt-10 bg-white border border-[#e2e8f0] rounded-lg p-6 flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#64748b]">Total</div>
              <div className="font-display font-black text-3xl text-[#0f1b2d]">${Math.round(order.amount)}</div>
            </div>
            {isPaid ? (
              <Link to="/dashboard" className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-6 py-3 rounded-md">
                Go to dashboard <ArrowRight className="w-4 h-4" />
              </Link>
            ) : (
              <button
                onClick={startPayment}
                disabled={paying || !allComplete}
                data-testid="review-continue-payment"
                className="inline-flex items-center gap-2 bg-[#2563eb] hover:bg-[#1d4ed8] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold px-6 py-3 rounded-md"
              >
                {paying ? "Redirecting…" : "Continue to payment"} <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
          {!allComplete && !isPaid && !isCustomOrder && (
            <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">
              Complete every applicant&apos;s details to proceed to payment.
            </p>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-[#64748b]">{label}</div>
      <div className="mt-1 font-semibold text-[#0f1b2d] flex items-center gap-1.5">
        {Icon && <Icon className="w-4 h-4 text-[#2563eb]" />}
        <span>{value}</span>
      </div>
    </div>
  );
}

export function ApplicantBreakdown({ a }) {
  const isMinor = a.minor_applicant === "yes" || a.guardian_name || a.guardian_email || a.guardian_phone;
  const destinations = (a.destination_countries || []).filter(Boolean);
  return (
    <div className="border-t border-[#e2e8f0] p-5 grid grid-cols-1 lg:grid-cols-2 gap-6">
      <SectionBlock title="Personal Information">
        <BreakdownRow label="Surname" value={a.surname} />
        <BreakdownRow label="Surname at birth" value={a.surname_at_birth} />
        <BreakdownRow label="First name(s)" value={a.first_names} />
        <BreakdownRow label="Date of birth" value={a.date_of_birth} />
        <BreakdownRow label="Place of birth" value={a.place_of_birth} />
        <BreakdownRow label="Country of birth" value={a.country_of_birth} />
        <BreakdownRow label="Current nationality" value={a.nationality} />
        <BreakdownRow label="Nationality at birth" value={a.nationality_at_birth} />
        <BreakdownRow label="Other nationality" value={a.nationality_other} />
        <BreakdownRow label="Sex" value={a.sex} />
        <BreakdownRow label="Marital status" value={a.civil_status} />
        <BreakdownRow label="Minor applicant" value={a.minor_applicant ? a.minor_applicant.toUpperCase() : ""} />
        <BreakdownRow label="Applicant ID number" value={a.applicant_id_number} />
      </SectionBlock>
      <SectionBlock title="Passport / Travel Document">
        <BreakdownRow label="Type" value={a.travel_document_type} />
        <BreakdownRow label="Passport number" value={a.passport_number} />
        <BreakdownRow label="Date of issue" value={a.passport_issue_date} />
        <BreakdownRow label="Valid until" value={a.passport_expiry_date} />
        <BreakdownRow label="Issued country" value={a.passport_issued_country} />
        <BreakdownRow label="Issued by (authority)" value={a.passport_issued_by_authority} />
      </SectionBlock>
      <SectionBlock title="Address, Contact & Occupation">
        <BreakdownRow label="Home address" value={a.home_address} />
        <BreakdownRow label="Street" value={a.application_street} />
        <BreakdownRow label="City" value={a.application_city} />
        <BreakdownRow label="Country" value={a.applicant_country} />
        <BreakdownRow label="Postal code" value={a.applicant_postal_code} />
        <BreakdownRow label="Email" value={a.email} />
        <BreakdownRow label="Phone" value={a.phone} />
        <BreakdownRow label="Occupation" value={a.occupation === "Other" ? `Other — ${a.occupation_other || "?"}` : a.occupation} />
        <BreakdownRow label="Residence in different country" value={a.residence_different ? a.residence_different.toUpperCase() : ""} />
        {a.residence_different === "yes" && <>
          <BreakdownRow label="Country of residence" value={a.residence_country} />
          <BreakdownRow label="Residence permit no." value={a.residence_permit_no} />
          <BreakdownRow label="Permit valid until" value={a.residence_permit_valid_until} />
        </>}
      </SectionBlock>
      {a.occupation !== "No occupation" && (
        <SectionBlock title="Employer / Education">
          <BreakdownRow label="Name" value={a.employer_name} />
          <BreakdownRow label="Address" value={a.employer_address} />
          <BreakdownRow label="Phone" value={a.employer_phone} />
          <BreakdownRow label="City" value={a.employer_city} />
          <BreakdownRow label="Postal code" value={a.employer_postal_code} />
          <BreakdownRow label="Country" value={a.employer_country} />
        </SectionBlock>
      )}
      <SectionBlock title="Travel Details">
        <BreakdownRow label="Purpose" value={a.purpose} />
        <BreakdownRow label="Purpose info" value={a.purpose_additional_info} />
        <BreakdownRow label="First entry country" value={a.first_entry_country} />
        <BreakdownRow label="Destinations" value={destinations.length ? destinations.join(", ") : "—"} />
        {Array.isArray(a.destination_nights) && a.destination_nights.length > 1 && (
          <BreakdownRow
            label="Nights per country"
            value={a.destination_nights
              .filter((n) => n && n.country)
              .map((n) => `${n.country}: ${n.nights || 0} ${(parseInt(n.nights, 10) || 0) === 1 ? "night" : "nights"}`)
              .join(", ")}
          />
        )}
        <BreakdownRow label="Intended arrival" value={a.intended_arrival_date} />
        <BreakdownRow label="Intended departure" value={a.intended_departure_date} />
        <BreakdownRow label="Total days" value={(() => { const d = totalDays(a.intended_arrival_date, a.intended_departure_date); return d ? `${d} day${d === 1 ? "" : "s"}` : "—"; })()} />
        <BreakdownRow label="Number of entries" value={a.number_of_entries} />
        <BreakdownRow label="Travel insurance ack." value={a.travel_insurance_ack ? a.travel_insurance_ack.toUpperCase() : ""} />
        {a.relationship && <BreakdownRow label="Relationship" value={a.relationship} />}
      </SectionBlock>
      <SectionBlock title="Previous Visa & Fingerprints">
        <BreakdownRow label="Schengen visa in past 3 years" value={a.previous_visa_3y ? a.previous_visa_3y.toUpperCase() : ""} />
        {a.previous_visa_3y === "yes" && <>
          <BreakdownRow label="Validity from" value={a.previous_visa_validity_from} />
          <BreakdownRow label="Validity until" value={a.previous_visa_validity_until} />
        </>}
        <BreakdownRow label="Fingerprints collected" value={a.fingerprints_collected ? a.fingerprints_collected.toUpperCase() : ""} />
        {a.fingerprints_collected === "yes" && <>
          <BreakdownRow label="Fingerprints date" value={a.fingerprints_date} />
          <BreakdownRow label="Issuing country" value={a.fingerprints_sticker_country} />
          <BreakdownRow label="Sticker number" value={a.fingerprints_sticker_number} />
        </>}
      </SectionBlock>
      <SectionBlock title="Cost & Support">
        <BreakdownRow label="Cost covered by" value={a.cost_covered_by === "Other" ? `Other — ${a.cost_covered_by_other || "?"}` : a.cost_covered_by} />
        <BreakdownRow label="Means of support" value={(() => {
          const ms = Array.isArray(a.means_of_support) ? a.means_of_support : (a.means_of_support ? [a.means_of_support] : []);
          if (!ms.length) return "—";
          return ms.map((m) => m === "Other" ? `Other — ${a.means_of_support_other || "?"}` : m).join(", ");
        })()} />
        <BreakdownRow label="Inviting person covers costs" value={a.inviting_costs ? a.inviting_costs.toUpperCase() : ""} />
        {a.inviting_costs === "yes" && <BreakdownRow label="Inviting party" value={a.inviting_covered_by} />}
      </SectionBlock>
      {a.has_eu_family === "yes" && (
        <SectionBlock title="EU/EEA/CH/UK Family Member">
          <BreakdownRow label="Surname" value={a.eu_family_surname} />
          <BreakdownRow label="First name" value={a.eu_family_first_name} />
          <BreakdownRow label="Nationality" value={a.eu_family_nationality} />
          <BreakdownRow label="Date of birth" value={a.eu_family_dob} />
          <BreakdownRow label="Passport number" value={a.eu_family_passport} />
          <BreakdownRow label="Relationship" value={a.eu_family_relationship === "Other" ? (a.eu_family_relationship_other || "Other") : a.eu_family_relationship} />
        </SectionBlock>
      )}
      {isMinor && (
        <SectionBlock title="Parent / Guardian">
          <BreakdownRow label="Guardian name" value={a.guardian_name} />
          <BreakdownRow label="Guardian address" value={a.guardian_address} />
          <BreakdownRow label="Guardian phone" value={a.guardian_phone} />
          <BreakdownRow label="Guardian email" value={a.guardian_email} />
          <BreakdownRow label="Guardian nationality" value={a.guardian_nationality} />
        </SectionBlock>
      )}
    </div>
  );
}

function BreakdownRow({ label, value }) {
  return (
    <div className="flex items-start gap-2 text-sm">
      <span className="text-[#64748b] w-44 flex-shrink-0">{label}</span>
      <span className="text-[#0f1b2d] font-medium break-words">{value || <span className="text-[#94a3b8] italic">—</span>}</span>
    </div>
  );
}

function SectionBlock({ title, children }) {
  return (
    <div>
      <h4 className="text-xs uppercase tracking-widest text-[#2563eb] font-semibold mb-2">{title}</h4>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}
